import React, { FC, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Button } from "components/buttons/Button"

import { tagsService } from "services/tags/tags.service"
import { RuleFieldDef, Tag, TagRule, TagWithUsage } from "services/tags/type"

import { AutoRuleTab, LinkedTag } from "./AutoRuleTab"
import { ManualTagTab } from "./ManualTagTab"

type Props = {
  open: boolean
  defaultTab: "manual" | "auto"
  onClose: () => void
  onSaved: () => void
  editManualTag: TagWithUsage | null
  editRule: TagRule | null
  availableAutomaticTags: TagWithUsage[]
  fields: RuleFieldDef[]
}

export const ModalCreateTag: FC<Props> = ({
  open,
  defaultTab,
  onClose,
  onSaved,
  editManualTag,
  editRule,
  availableAutomaticTags,
  fields
}) => {
  const isEditManual = !!editManualTag?.id
  const isEditRule = !!editRule?.id
  const { t } = useTranslation("translation", { useSuspense: false })

  const [tab, setTab] = useState<"manual" | "auto">(defaultTab)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setTab(defaultTab)
  }, [open, defaultTab])

  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false
      return
    }
    if (!wasOpenRef.current) {
      setSaving(false)
      wasOpenRef.current = true
    }
  }, [open])

  // The rule's linked tag, from the catalogue when it is loaded and from the rule's own
  // embedded copy otherwise. Its attributes are editable while editing the rule; which tag the
  // rule points at is not.
  const linkedTag = useMemo<LinkedTag | null>(() => {
    const tagId = editRule?.tag_id
    if (!tagId) return null
    const fromCatalogue = availableAutomaticTags.find((t) => t.id === tagId)
    if (fromCatalogue) return fromCatalogue
    if (editRule?.tag) return editRule.tag
    return { id: tagId, name: tagId, slug: "" }
  }, [availableAutomaticTags, editRule])

  if (!open) return null

  const saveManual = async (payload: Partial<Tag>) => {
    setSaving(true)
    try {
      const res = isEditManual ? await tagsService.updateTag(editManualTag!.id, payload) : await tagsService.createTag(payload)
      if (res) {
        toast.success(t(isEditManual ? "tags.toast.manualUpdated" : "tags.toast.manualCreated") as string)
        onSaved()
      }
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? (t("tags.toast.manualSaveFailed") as string))
    } finally {
      setSaving(false)
    }
  }

  const saveAutoRule = async (payload: any) => {
    setSaving(true)
    try {
      // Editing a rule can also rename its tag — two entities, two endpoints. The tag goes
      // first so a rejected rename leaves the rule untouched rather than half-applied, and
      // `updateTag` has already surfaced the reason when it returns nothing.
      if (isEditRule && payload.tag) {
        const { id: tagId, ...tagPatch } = payload.tag
        const tagRes = await tagsService.updateTag(tagId, tagPatch)
        if (!tagRes) return
      }
      const { tag: _tag, ...rulePayload } = payload
      const res = isEditRule ? await tagsService.updateRule(editRule!.id, rulePayload) : await tagsService.createRule(payload)
      if (res) {
        toast.success(t(isEditRule ? "tags.toast.ruleUpdated" : "tags.toast.ruleCreated") as string)
        onSaved()
      }
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? (t("tags.toast.ruleSaveFailed") as string))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[1000] grid place-items-center bg-black/40 p-4">
      <div className="w-full max-w-6xl rounded-2xl bg-white dark:bg-[#0C0C1A] border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] p-4 text-black dark:text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-semibold">
            {tab === "manual"
              ? isEditManual
                ? t("tags.editManualTag")
                : t("tags.createManualTag")
              : isEditRule
              ? t("tags.editAutomaticRule")
              : t("tags.createAutomaticRule")}
          </div>

          <Button size="sm" onClick={onClose} disabled={saving}>
            {t("common.close")}
          </Button>
        </div>

        <div className="mt-3 flex gap-2">
          <Button size="sm" variant={tab === "manual" ? "primary" : "outline"} onClick={() => setTab("manual")} disabled={saving}>
            {t("tags.manual")}
          </Button>

          <Button size="sm" variant={tab === "auto" ? "primary" : "outline"} onClick={() => setTab("auto")} disabled={saving}>
            {t("tags.automaticRule")}
          </Button>
        </div>

        {tab === "manual" ? (
          <ManualTagTab key={editManualTag?.id ?? "new"} saving={saving} editTag={editManualTag} onSave={saveManual} />
        ) : (
          <AutoRuleTab
            key={editRule?.id ?? "new"}
            saving={saving}
            fields={fields}
            editRule={editRule}
            linkedTag={linkedTag}
            onSave={saveAutoRule}
          />
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </div>
  )
}
